import React, { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Calendar,
  Clock,
  Building,
  User,
  Mail,
  MessageSquare,
  CheckCircle,
  Loader2,
} from "lucide-react";
// import { supabase } from "@/lib/supabase";
import { API_BASE_URL } from "@/config";
import Turnstile from "@/components/common/Turnstile";

interface RequestDemoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const RequestDemoModal = ({ isOpen, onClose }: RequestDemoModalProps) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    company: "",
    phone: "",
    role: "",
    preferredDate: "",
    preferredTime: "",
    interests: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const timeSlots = [
    "09:00 AM",
    "10:00 AM",
    "11:00 AM",
    "12:00 PM",
    "01:00 PM",
    "02:00 PM",
    "03:00 PM",
    "04:00 PM",
    "05:00 PM",
  ];

  const roles = [
    "Abogado Independiente",
    "Socio de Firma",
    "Asociado",
    "Estudiante de Derecho",
    "Empresa Legal",
    "Otro",
  ];

  const interests = [
    "Chatbot Legal",
    "Análisis de Documentos",
    "Investigación Legal",
    "Predicción de Casos",
    "Generación de Contratos",
    "Monitoreo de Cumplimiento",
    "Búsqueda de Patentes",
    "Asistente al Consumidor",
  ];

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.fullName.trim()) {
      errors.fullName = "El nombre es requerido";
    }

    if (!formData.email.trim()) {
      errors.email = "El email es requerido";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Email inválido";
    }

    if (!formData.company.trim()) {
      errors.company = "La empresa es requerida";
    }

    if (!formData.role) {
      errors.role = "El rol es requerido";
    }

    if (!formData.preferredDate) {
      errors.preferredDate = "La fecha es requerida";
    }

    if (!formData.preferredTime) {
      errors.preferredTime = "La hora es requerida";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Call backend to send emails (confirmation, admin notification, reminder)
      const demoData = {
        fullName: formData.fullName,
        email: formData.email,
        company: formData.company,
        phone: formData.phone,
        role: formData.role,
        preferredDate: formData.preferredDate,
        preferredTime: formData.preferredTime,
        interests: formData.interests,
        message: formData.message,
      };

      // Single backend endpoint; backend will handle sending all related emails
      const endpoint = `${API_BASE_URL.replace(
        /\/$/,
        ""
      )}/api/request-demo/send`;
      try {
        // RequestDemoModal: calling endpoint
        const res = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ demoData }),
        });
        if (!res.ok) {
          const text = await res.text().catch(() => "");
          console.error("/api/request-demo/send failed", res.status, text);
          toast({
            title: "Aviso",
            description:
              "Se registró la solicitud, pero no pudimos notificar por email. Intentaremos nuevamente más tarde.",
          });
        }
      } catch (err) {
        console.error("Error calling /api/request-demo/send", err);
        toast({
          title: "Aviso",
          description:
            "Se registró la solicitud, pero hubo un problema al contactar el servicio de email.",
        });
      }

      toast({
        title: "¡Solicitud de Demo Enviada!",
        description:
          "Nos pondremos en contacto contigo pronto para confirmar los detalles de tu demo personalizada.",
      });

      // Reset form
      setFormData({
        fullName: "",
        email: "",
        company: "",
        phone: "",
        role: "",
        preferredDate: "",
        preferredTime: "",
        interests: "",
        message: "",
      });
      onClose();
    } catch (error) {
      console.error("Error submitting demo request:", error);
      toast({
        title: "Error",
        description:
          "No se pudo enviar la solicitud. Por favor, inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-legal-navy flex items-center gap-2">
            <Calendar className="h-6 w-6 text-legal-blue" />
            Solicitar Demo Personalizada
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Descubre cómo nuestra IA legal puede transformar tu práctica
            jurídica. Agenda una demostración personalizada con nuestros
            expertos.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <User className="h-5 w-5 text-legal-blue" />
              Información Personal
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Nombre Completo *</Label>
                <Input
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Tu nombre completo"
                  className={formErrors.fullName ? "border-red-500" : ""}
                />
                {formErrors.fullName && (
                  <p className="text-sm text-red-500">{formErrors.fullName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="tu@email.com"
                  className={formErrors.email ? "border-red-500" : ""}
                />
                {formErrors.email && (
                  <p className="text-sm text-red-500">{formErrors.email}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Rol/Posición *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => handleSelectChange("role", value)}>
                  <SelectTrigger
                    className={formErrors.role ? "border-red-500" : ""}>
                    <SelectValue placeholder="Selecciona tu rol" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formErrors.role && (
                  <p className="text-sm text-red-500">{formErrors.role}</p>
                )}
              </div>
            </div>
          </div>

          {/* Company Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Building className="h-5 w-5 text-legal-blue" />
              Información de la Empresa
            </h3>

            <div className="space-y-2">
              <Label htmlFor="company">Empresa/Organización *</Label>
              <Input
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                placeholder="Nombre de tu empresa"
                className={formErrors.company ? "border-red-500" : ""}
              />
              {formErrors.company && (
                <p className="text-sm text-red-500">{formErrors.company}</p>
              )}
            </div>
          </div>

          {/* Demo Scheduling */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="h-5 w-5 text-legal-blue" />
              Programación de la Demo
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="preferredDate">Fecha Preferida *</Label>
                <Input
                  id="preferredDate"
                  name="preferredDate"
                  type="date"
                  value={formData.preferredDate}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split("T")[0]}
                  className={formErrors.preferredDate ? "border-red-500" : ""}
                />
                {formErrors.preferredDate && (
                  <p className="text-sm text-red-500">
                    {formErrors.preferredDate}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="preferredTime">Hora Preferida *</Label>
                <Select
                  value={formData.preferredTime}
                  onValueChange={(value) =>
                    handleSelectChange("preferredTime", value)
                  }>
                  <SelectTrigger
                    className={
                      formErrors.preferredTime ? "border-red-500" : ""
                    }>
                    <SelectValue placeholder="Selecciona una hora" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formErrors.preferredTime && (
                  <p className="text-sm text-red-500">
                    {formErrors.preferredTime}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Interests */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-legal-blue" />
              Intereses Específicos
            </h3>

            <div className="space-y-2">
              <Label htmlFor="interests">
                ¿Qué funcionalidades te interesan más?
              </Label>
              <Textarea
                id="interests"
                name="interests"
                value={formData.interests}
                onChange={handleInputChange}
                placeholder="Menciona las funcionalidades que más te interesan: chatbot legal, análisis de documentos, predicción de casos, etc."
                rows={3}
              />
            </div>
          </div>

          {/* Additional Message */}
          <div className="space-y-2">
            <Label htmlFor="message">Mensaje Adicional</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Cuéntanos más sobre tus necesidades específicas o preguntas que tengas..."
              rows={3}
            />
          </div>

          {/* Benefits Preview */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              ¿Qué incluye tu demo?
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Demostración personalizada de las funcionalidades de IA</li>
              <li>• Análisis de casos específicos de tu práctica</li>
              <li>• Respuestas a tus preguntas técnicas</li>
              <li>• Estrategias de implementación</li>
              <li>• Plan de precios personalizado</li>
            </ul>
          </div>

          <div className="flex justify-center my-4">
            <Turnstile onVerify={() => {}} />
          </div>

          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button
              type="submit"
              className="btn-gradient"
              disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                "Solicitar Demo"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RequestDemoModal;
